import {AuthTypesEnum} from "@/lib/enums/AuthTypesEnum";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";

/**
 * `DialogsEnum` → `popup_name` (AC-711, spec §9.1).
 *
 * Every dialog in the app flows through the orchestrator, so this map plus the
 * single subscriber in `<MixpanelDialogBridge/>` covers `popup_opened` /
 * `popup_closed` for all of them, desktop and mobile, with no edit to any dialog
 * component.
 *
 * The record is TOTAL on purpose: a 19th dialog added to `DialogsEnum` is a
 * compile error here until someone names its popup, so the bridge can never
 * silently under-report.
 */

export type PopupName =
    | "regional_restrictions"
    | "login"
    | "registration"
    | "log_out"
    | "set_new_password"
    | "bonus_conditions"
    | "continue_top_up"
    | "unlock_next_bonus"
    | "welcome_bonus"
    | "welcome_bonus_promo"
    | "free_spins_game"
    | "confirm"
    | "contact_verify"
    | "search"
    | "deposit_success"
    | "wager_requirement"
    | "cashier"
    | "tx_cancelled"
    | "pwa_install"
    | "push_permission"
    | "promo_code_result"
    | "delete_account";

const DIALOG_TO_POPUP_NAME: Record<DialogsEnum, PopupName> = {
    [DialogsEnum.REGIONAL_RESTRICTIONS]: "regional_restrictions",
    // Placeholder: AUTH renders either tab, so its name is resolved from the
    // request's `tab` prop below. This entry is the fallback when it is absent.
    [DialogsEnum.AUTH]: "registration",
    [DialogsEnum.LOG_OUT]: "log_out",
    [DialogsEnum.SET_NEW_PASSWORD]: "set_new_password",
    [DialogsEnum.BONUS_CONDITIONS]: "bonus_conditions",
    [DialogsEnum.CONTINUE_TOP_UP]: "continue_top_up",
    [DialogsEnum.UNLOCK_NEXT_BONUS]: "unlock_next_bonus",
    [DialogsEnum.WELCOME_BONUS]: "welcome_bonus",
    [DialogsEnum.WELCOME_BONUS_PROMO]: "welcome_bonus_promo",
    [DialogsEnum.FREE_SPINS_GAME]: "free_spins_game",
    [DialogsEnum.CONFIRM]: "confirm",
    [DialogsEnum.CONTACT_VERIFY]: "contact_verify",
    [DialogsEnum.SEARCH]: "search",
    // The post-deposit success popup — the spec's `deposit_success` popup_name.
    [DialogsEnum.FUNDS_CREDITED]: "deposit_success",
    [DialogsEnum.WAGER_REQUIREMENT]: "wager_requirement",
    [DialogsEnum.WALLET]: "cashier",
    [DialogsEnum.TX_CANCELLED]: "tx_cancelled",
    [DialogsEnum.PWA_INSTALL]: "pwa_install",
    // Web-push soft prompt (AC-649). It yields to any other dialog, so one login
    // can report several open/close pairs with `close_type: system`.
    [DialogsEnum.PUSH_PERMISSION]: "push_permission",
    [DialogsEnum.PROMO_CODE_RESULT]: "promo_code_result",
    // AC-830 account soft-delete confirmation (reached from /account/delete only).
    [DialogsEnum.DELETE_ACCOUNT]: "delete_account",
};

/**
 * Resolves the popup name for a dialog instance. AUTH is the one dialog whose
 * name depends on how it was opened: the same component is the login popup and
 * the registration popup, and the funnel needs them apart.
 *
 * `props` is the untyped bag from `orchestrator.open(id, {props})`, so `tab` is
 * validated rather than cast.
 */
export const resolvePopupName = (
    id: DialogsEnum,
    props?: Record<string, unknown>,
): PopupName => {
    if (id === DialogsEnum.AUTH) {
        return props?.tab === AuthTypesEnum.LOGIN ? "login" : "registration";
    }
    return DIALOG_TO_POPUP_NAME[id];
};
